import {Inject, Injectable} from '@angular/core';
import {HttpClient, HttpHeaders} from "@angular/common/http";
import {Observable} from "rxjs";
import {CompanyAliasResult} from "../../models/admin_owner/company/company-alias-result";
import {CompanyDto} from "../../models/admin_owner/company/company-dto";
import {ServerEndpoints} from "../../server.endpoints";
import {Address} from "../../models/common/address";

@Injectable({
  providedIn: 'root'
})
export class CompanyHttpService {

  constructor(private httpClient: HttpClient,
              @Inject("BASE_URL") private baseUrl: string,
              @Inject("BASE_HEADERS") private baseHeaders: HttpHeaders) {
  }

  list(): Observable<CompanyDto[]> {
    return this.httpClient.get<CompanyDto[]>(this.baseUrl + ServerEndpoints.Management_Company_List, {headers: this.baseHeaders});
  }

  get(id: string): Observable<CompanyDto> {
    return this.httpClient.get<CompanyDto>(this.baseUrl + ServerEndpoints.Management_Company_Get + "?id=" + id, {headers: this.baseHeaders});
  }

  delete(id: string) {
    return this.httpClient.delete(this.baseUrl + ServerEndpoints.Management_Company_Delete + "?id=" + id, {headers: this.baseHeaders});
  }

  changeName(id: string, name: string): Observable<CompanyDto> {
    return this.httpClient.put<CompanyDto>(this.baseUrl + ServerEndpoints.Management_Company_ChangeName, {
      "Id": id,
      "Name": name
    }, {headers: this.baseHeaders});
  }

  /**
   * Sets (or, with a blank value, clears) the company alias.
   *
   * Answers a {@link CompanyAliasResult} rather than the bare company since #637: the alias now names DNS
   * records, and a failure to register them does **not** fail the save — it comes back as `dnsWarning` on a
   * 200. A refused *alias* is still an error (`E_COMPANY_ALIAS_INVALID_LABEL`, 400); the two are different
   * outcomes and the screen has to report them differently.
   *
   * The value is sent as a query parameter and therefore encoded here: an alias is a DNS label, so nothing in
   * the valid set needs escaping — but an invalid one may, and it has to reach the server intact to be
   * refused with a sentence rather than mangled into something the rule happens to accept.
   */
  changeAlias(id: string, alias: string): Observable<CompanyAliasResult> {
    return this.httpClient.put<CompanyAliasResult>(
      this.baseUrl + ServerEndpoints.Management_Company_SetAlias
      + "?id=" + encodeURIComponent(id) + "&alias=" + encodeURIComponent(alias), {});
  }

  setAddress(id: string, address: Address): Observable<CompanyDto> {
    if (address.street == "") address.street = undefined!;
    if (address.additionalAddressLine == "") address.additionalAddressLine = undefined!;
    if (address.zip == "") address.zip = undefined!;
    if (address.city == "") address.city = undefined!;
    return this.httpClient.patch<CompanyDto>(`${this.baseUrl}${ServerEndpoints.Management_Company_SetAddress_$id$.dictReplace({id: id})}`, address);
  }

  setProperty(id: string, variable: string, value: string): Observable<CompanyDto> {
    return this.httpClient.post<CompanyDto>(`${this.baseUrl}${ServerEndpoints.Management_Company_SetMetadata_$id$.dictReplace({id: id})}`, {
      name: variable,
      value: value
    })
  }
}
